import React, {useEffect, useRef, useState} from "react";
import {useAuth} from "@/context/AuthContext";
import {api, API, formatApiError} from "@/lib/api";
import "@/product-reviews.css";

const MAX_BYTES = 5 * 1024 * 1024;
const imageUrl = path => API.replace(/\/api\/?$/, "") + path;

function ReviewCard({review, children}) {
  return <article className="review-card">
    <div className="review-card-heading"><strong>{review.display_name}</strong>
      <span aria-label={`${review.rating} out of 5 stars`}>{"★".repeat(review.rating)}<span aria-hidden="true">{"☆".repeat(5 - review.rating)}</span></span>
    </div>
    {review.verified_purchase && <p className="review-verified">Verified purchase</p>}
    <h3>{review.title}</h3><p className="review-text">{review.text}</p>
    {review.photos.length > 0 && <div className="review-photos">{review.photos.map((photo, index) =>
      <a key={photo.url} href={imageUrl(photo.url)} target="_blank" rel="noopener noreferrer" aria-label={`Open customer photo ${index + 1} for ${review.title}`}>
        <img src={imageUrl(photo.url)} alt={`Customer photo ${index + 1} accompanying ${review.title}`} width={photo.width} height={photo.height} loading="lazy" />
      </a>)}</div>}
    <time dateTime={review.created_at}>{new Date(review.created_at).toLocaleDateString("en-GB")}</time>
    {children}
  </article>;
}

export default function ProductReviews({productId}) {
  const {user, openAuth} = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(undefined);
  const [pending, setPending] = useState({reviews: [], total: 0});
  const [page, setPage] = useState(1);
  const [queuePage, setQueuePage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [form, setForm] = useState({rating: "5", title: "", text: "", consent: false});
  const [reasons, setReasons] = useState({});
  const fileInput = useRef(null);
  const previewOnly = process.env.REACT_APP_DESIGN_PREVIEW === "true";
  const admin = user?.role === "admin";

  useEffect(() => {
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [files]);

  useEffect(() => {
    if (previewOnly) return undefined;
    const controller = new AbortController();
    const options = {signal: controller.signal};
    setError(""); setData(null); setMine(undefined);
    const encoded = encodeURIComponent(productId);
    Promise.all([
      api.get(`/products/${encoded}/reviews?page=${page}`, options),
      user?.id ? api.get(`/products/${encoded}/reviews/mine`, options) : Promise.resolve({data: {review: null}}),
      admin ? api.get(`/admin/reviews?product_id=${encoded}&page=${queuePage}`, options) : Promise.resolve({data: {reviews: [], total: 0}}),
    ]).then(([publicData, ownData, queue]) => {
      if (controller.signal.aborted) return;
      setData(publicData.data); setMine(ownData.data.review); setPending(queue.data);
    }).catch(err => {
      if (!controller.signal.aborted) setError(formatApiError(err.response?.data?.detail));
    });
    return () => controller.abort();
  }, [productId, user?.id, admin, revision, page, queuePage, previewOnly]);

  const choosePhotos = event => {
    const selected = Array.from(event.target.files || []);
    if (selected.length > 3 || selected.some(file => file.size > MAX_BYTES || !["image/jpeg", "image/png", "image/webp"].includes(file.type))) {
      setError("Choose up to three JPEG, PNG or WebP photos, no larger than 5 MB each.");
      event.target.value = ""; setFiles([]); return;
    }
    setError(""); setFiles(selected);
  };

  const act = async callback => {
    if (busy) return;
    setBusy(true); setError(""); setNotice("");
    try {await callback(); setRevision(value => value + 1);}
    catch (err) {setError(formatApiError(err.response?.data?.detail));}
    finally {setBusy(false);}
  };
  const submit = event => {
    event.preventDefault();
    act(async () => {
      const payload = new FormData();
      for (const [key, value] of Object.entries(form)) payload.append(key, String(value));
      files.forEach(file => payload.append("photos", file));
      const response = await api.post(`/products/${encodeURIComponent(productId)}/reviews`, payload);
      setNotice(response.data.message); setFiles([]);
      setForm({rating: "5", title: "", text: "", consent: false});
      if (fileInput.current) fileInput.current.value = "";
    });
  };

  return <section id="product-reviews" className="product-reviews" aria-labelledby="reviews-title">
    <p className="review-eyebrow">MADE PART OF YOUR ROUTINE</p>
    <h2 id="reviews-title">Customer stories &amp; photos</h2>
    <p>Share your honest experience, from morning smoothies to your favourite bowls.</p>
    <p className="review-help">Reviews are checked for spam, personal information and inappropriate content before publication. Positive and negative experiences are welcome. Only purchases matched to a paid order receive a verified purchase label.</p>
    {previewOnly ? <p className="review-notice">Sign in to share your story and photos with LENIS.</p> : <>
      {error && <p role="alert" className="review-error">{error} <button type="button" disabled={busy} onClick={() => setRevision(value => value + 1)}>Refresh reviews</button></p>}
      {notice && <p role="status" className="review-notice">{notice}</p>}
      {!data && !error && <p role="status">Loading reviews…</p>}
      {data && <>
        {data.total ? <p className="review-summary">{data.average} / 5 · {data.total} published {data.total === 1 ? "review" : "reviews"}</p> : <p className="review-empty">No published reviews yet. Share the first story from your routine.</p>}
        <div className="review-grid">{data.reviews.map(review => <ReviewCard key={review.id} review={review} />)}</div>
        {data.total > 12 && <nav aria-label="Review pages" className="review-actions">
          <button disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous</button><span>Page {page}</span><button disabled={page * 12 >= data.total} onClick={() => setPage(value => value + 1)}>Next</button>
        </nav>}
      </>}
      {!user ? <button className="review-primary" onClick={() => openAuth("login")}>Sign in to write a review</button> : mine ? <div className="review-own">
        <h3>Your review: {mine.status}</h3>
        {mine.reason && <p>{mine.reason}</p>}
        {mine.status !== "withdrawn" && <button disabled={busy} onClick={() => act(async () => {await api.post(`/reviews/${mine.id}/withdraw`); setNotice("Your review and photos have been removed from public view.");})}>Withdraw my review and photos</button>}
        {mine.status === "withdrawn" && <p>Your review and photos are no longer public.</p>}
      </div> : mine === null && <form onSubmit={submit} className="review-form">
        <h3>Tell us about your routine</h3>
        <label>Rating<select value={form.rating} onChange={event => setForm({...form, rating: event.target.value})}>{[5,4,3,2,1].map(rating => <option key={rating} value={rating}>{rating} {rating === 1 ? "star" : "stars"}</option>)}</select></label>
        <label>Review title<input required minLength={3} maxLength={100} value={form.title} onChange={event => setForm({...form, title: event.target.value})} /></label>
        <label>Your experience<textarea required minLength={20} maxLength={2000} rows={5} value={form.text} onChange={event => setForm({...form, text: event.target.value})} aria-describedby="review-text-help" /></label>
        <p id="review-text-help" className="review-help">20–2,000 characters. Please avoid private contact details or claims that a product treats a medical condition.</p>
        <label>Add photos of your smoothie or bowl (optional)<input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={choosePhotos} aria-describedby="review-photo-help" /></label>
        <p id="review-photo-help" className="review-help">Up to 3 photos · 5 MB each · JPEG, PNG or WebP · up to 20 megapixels. Location metadata is removed before storage.</p>
        <div className="review-photos">{previews.map((url, index) => <div key={url}><img src={url} alt={`Selected review photo ${index + 1}`} /><button type="button" onClick={() => {setFiles(previous => previous.filter((_, i) => i !== index)); if (fileInput.current) fileInput.current.value = "";}}>Remove photo {index + 1}</button></div>)}</div>
        <label className="review-consent"><input type="checkbox" required checked={form.consent} onChange={event => setForm({...form, consent: event.target.checked})} /><span>I own these photos and agree that my review, first name and photos may appear publicly on LENIS product pages after moderation.</span></label>
        <button className="review-primary" type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit review"}</button>
      </form>}
      {admin && <section className="review-moderation" aria-label="Review moderation">
        <h3>Awaiting moderation ({pending.total})</h3>
        <p className="review-help">Apply the same rules to every rating. Reject only spam, private information, inappropriate content or unsupported medical claims.</p>
        {pending.reviews.map(review => <ReviewCard key={review.id} review={review}>
          <label>Moderation note<input maxLength={300} value={reasons[review.id] || ""} onChange={event => setReasons({...reasons, [review.id]: event.target.value})} /></label>
          <div className="review-actions">{["approved", "rejected"].map(status => <button key={status} disabled={busy} onClick={() => act(async () => {await api.post(`/admin/reviews/${review.id}/moderate`, {status, reason: reasons[review.id] || ""}); setNotice("Review " + status + ".");})}>{status === "approved" ? "Publish review" : "Reject review"}</button>)}</div>
        </ReviewCard>)}
        {pending.total > 12 && <nav className="review-actions" aria-label="Moderation pages"><button disabled={queuePage === 1} onClick={() => setQueuePage(value => value - 1)}>Previous</button><span>Page {queuePage}</span><button disabled={queuePage * 12 >= pending.total} onClick={() => setQueuePage(value => value + 1)}>Next</button></nav>}
      </section>}
    </>}
  </section>;
}

